import { useCallback, useMemo, useState, useEffect } from 'react';
import { POST_MESSAGES_TYPES } from 'modules/codeless-events/consts/iframeParentConstants';
import { setNewConversionsSessionStorage } from 'modules/codeless-events/utils/codelessSessionStorageUtils';
import { CONVERSIONS_TABS } from '../config/conversionTabs';
import { STEP_IDS } from '../config/stepIds';
import { useConversions } from './useConversions';
import { useDraftConversion } from './useDraftConversion';

const INITIAL_STEP_BY_TAB = {
    [CONVERSIONS_TABS.EVENTS_ON_THIS_PAGE]: STEP_IDS.VIEW_EVENTS_LIST,
    [CONVERSIONS_TABS.ALL_YOUR_EVENTS]: STEP_IDS.VIEW_EVENTS_LIST,
};

export const useTabStepNavigation = tabsConfig => {
    const [activeTabId, setActiveTabId] = useState(tabsConfig[0].id);
    const [currentStepId, setCurrentStepId] = useState(INITIAL_STEP_BY_TAB[activeTabId]);
    const [currentlyEventId, setCurrentlyEventId] = useState(null);
    const [isNavigateMode, setIsNavigateMode] = useState(false);

    const { persistentConversions, setPersistentConversions } = useConversions();
    const { draftEvent, initializeDraftForNew, initializeDraftForEdit, updateDraftEvent, clearAndResetDraft } =
        useDraftConversion();

    const activeTab = useMemo(() => tabsConfig.find(tab => tab.id === activeTabId), [tabsConfig, activeTabId]);

    useEffect(() => {
        if (currentStepId === STEP_IDS.VIEW_EVENTS_LIST) {
            setNewConversionsSessionStorage(persistentConversions);
        }
    }, [persistentConversions, currentStepId]);

    const currentConversion = useMemo(() => {
        if (currentlyEventId && draftEvent && draftEvent.id === currentlyEventId) {
            return draftEvent;
        }
        return null;
    }, [currentlyEventId, draftEvent]);

    const navigateToStepId = useCallback(newStepId => {
        setCurrentStepId(newStepId);
    }, []);

    const resetWizardToInitialStep = useCallback(() => {
        setCurrentStepId(INITIAL_STEP_BY_TAB[activeTabId]);
        setCurrentlyEventId(null);
        clearAndResetDraft();
    }, [activeTabId, clearAndResetDraft]);

    const switchTab = useCallback(
        newTabId => {
            setActiveTabId(newTabId);
            setCurrentStepId(INITIAL_STEP_BY_TAB[newTabId]);
            setCurrentlyEventId(null);
            setIsNavigateMode(false);
            clearAndResetDraft();
        },
        [clearAndResetDraft]
    );

    const handleStartCreateNewEvent = useCallback(() => {
        const newDraftId = initializeDraftForNew();
        setCurrentlyEventId(newDraftId);
        navigateToStepId(STEP_IDS.CONVERSION_TYPE_SETUP);
    }, [initializeDraftForNew, navigateToStepId]);

    const handleEditEvent = useCallback(
        (eventIdToEdit, targetStepId = STEP_IDS.CONVERSION_TYPE_SETUP) => {
            const eventFromPersistent = persistentConversions.find(event => event.id === eventIdToEdit);
            if (eventFromPersistent) {
                initializeDraftForEdit(eventFromPersistent);
                setCurrentlyEventId(eventIdToEdit);
                navigateToStepId(targetStepId);
            } else {
                clearAndResetDraft();
                resetWizardToInitialStep();
            }
        },
        [
            persistentConversions,
            initializeDraftForEdit,
            navigateToStepId,
            clearAndResetDraft,
            resetWizardToInitialStep,
            setCurrentlyEventId,
        ]
    );

    const handleCancelWizard = useCallback(() => {
        clearAndResetDraft();
        resetWizardToInitialStep();
        setIsNavigateMode(false);
    }, [clearAndResetDraft, resetWizardToInitialStep]);

    const handleSaveDraftToPersistent = useCallback(() => {
        if (!draftEvent || !draftEvent.id) {
            clearAndResetDraft();
            navigateToStepId(STEP_IDS.VIEW_EVENTS_LIST);
            return;
        }

        const eventExistsInPersistent = persistentConversions.some(event => event.id === draftEvent.id);

        if (eventExistsInPersistent) {
            setPersistentConversions(prevPersistent =>
                prevPersistent.map(event => (event.id === draftEvent.id ? draftEvent : event))
            );
        } else {
            setPersistentConversions(prevPersistent => [...prevPersistent, draftEvent]);
        }

        clearAndResetDraft();
        navigateToStepId(STEP_IDS.VIEW_EVENTS_LIST);
    }, [draftEvent, persistentConversions, setPersistentConversions, clearAndResetDraft, navigateToStepId]);

    const handleNavigateMode = useCallback(() => {
        setIsNavigateMode(prev => {
            const { FINISH_NAVIGATE, NAVIGATE_MODE } = POST_MESSAGES_TYPES;
            window.parent.postMessage({ type: prev ? FINISH_NAVIGATE : NAVIGATE_MODE }, '*');
            return !prev;
        });
    }, []);

    return {
        activeTabId,
        activeTab,
        currentStepId,
        currentConversion,
        currentlyEventId,
        updateDraftEvent,
        handleStartCreateNewEvent,
        handleEditEvent,
        handleCancelWizard,
        handleSaveDraftToPersistent,
        navigateToStepId,
        resetWizardToInitialStep,
        switchTab,
        INITIAL_STEP_BY_TAB,
        isNavigateMode,
        handleNavigateMode,
    };
};
